import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, BookOpen, Building2, FileText, MapPin, Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { findSearchResults } from "../data/searchCatalog";
import { trackEvent } from "../utils/leads";

const typeIcon = {
  Service: Building2,
  Location: MapPin,
  Guide: FileText,
  Article: BookOpen,
};

export const GlobalSearch = ({ open, onClose }) => {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const dialogRef = useRef(null);
  const navigate = useNavigate();
  const results = useMemo(() => findSearchResults(query), [query]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.setTimeout(() => inputRef.current?.focus(), 40);
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose, open]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  useEffect(() => setActiveIndex(0), [query]);

  if (!open) return null;

  const selectResult = (item) => {
    trackEvent("site_search", {
      search_term: query.slice(0, 80),
      search_result: item.id,
      result_type: item.type,
    });
    onClose();
    navigate(item.path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDialogKeyDown = (event) => {
    if (event.key === "ArrowDown" && results.length) {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % results.length);
      return;
    }
    if (event.key === "ArrowUp" && results.length) {
      event.preventDefault();
      setActiveIndex((current) => (current - 1 + results.length) % results.length);
      return;
    }
    if (event.key === "Enter" && results[activeIndex]) {
      event.preventDefault();
      selectResult(results[activeIndex]);
      return;
    }
    if (event.key === "Tab") {
      const focusable = dialogRef.current?.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), a[href]',
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-slate-950/55 p-3 pt-20 backdrop-blur-sm sm:p-6 sm:pt-24" role="presentation" onMouseDown={onClose}>
      <section ref={dialogRef} className="mx-auto w-full max-w-2xl overflow-hidden rounded-3xl border border-white/50 bg-white shadow-2xl" role="dialog" aria-modal="true" aria-label="Find a BizSarthi service" onKeyDown={handleDialogKeyDown} onMouseDown={(event) => event.stopPropagation()}>
        <div className="border-b border-slate-200 p-4 sm:p-5">
          <div className="flex items-center gap-3">
            <Search className="h-5 w-5 flex-shrink-0 text-emerald-600" />
            <input ref={inputRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search GST, LLP, Virtual Office, Gurugram..." className="min-w-0 flex-1 bg-transparent text-base text-slate-900 outline-none placeholder:text-slate-400 sm:text-lg" aria-label="Search services and locations" role="combobox" aria-expanded="true" aria-controls="bizsarthi-search-results" aria-activedescendant={results[activeIndex] ? `search-result-${results[activeIndex].id}` : undefined} />
            <button type="button" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100" aria-label="Close search"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="max-h-[65vh] overflow-y-auto p-3 sm:p-4">
          {!query.trim() && (
            <div className="rounded-2xl bg-gradient-to-r from-emerald-50 to-blue-50 p-5">
              <p className="font-semibold text-slate-900">Find the right service quickly</p>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">Search by service, registration type, State, city or a familiar term such as Pvt Ltd, GST filling, Gurgaon or food licence.</p>
            </div>
          )}
          {query.trim() && results.length === 0 && (
            <div className="p-7 text-center">
              <p className="font-semibold text-slate-900">No exact match found</p>
              <p className="mt-2 text-sm text-slate-600">Try a shorter term or contact the BizSarthi team for help.</p>
            </div>
          )}
          <div id="bizsarthi-search-results" className="space-y-2" role="listbox" aria-label="Search results">
            {results.map((item, index) => {
              const Icon = typeIcon[item.type] || FileText;
              return (
                <button id={`search-result-${item.id}`} key={item.id} type="button" role="option" aria-selected={index === activeIndex} onMouseEnter={() => setActiveIndex(index)} onClick={() => selectResult(item)} className={`group flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition ${index === activeIndex ? "border-emerald-200 bg-emerald-50/80" : "border-transparent hover:border-emerald-100 hover:bg-emerald-50/70"}`}>
                  <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700 group-hover:bg-white group-hover:text-emerald-700"><Icon className="h-5 w-5" /></span>
                  <span className="min-w-0 flex-1"><span className="block truncate font-semibold text-slate-900">{item.title}</span><span className="mt-0.5 block text-xs font-medium uppercase tracking-wide text-slate-400">{item.type}</span></span>
                  <ArrowRight className="h-4 w-4 flex-shrink-0 text-slate-400 transition group-hover:translate-x-1 group-hover:text-emerald-600" />
                </button>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};
